/**
 * <ls-series-models>: series hub model list (sections/ls-series-models.liquid, SERIES lane).
 * Progressive: the table works without script; this adds column sorting (header buttons, aria-sort), a text
 * filter over model + key values, and the table / image-grid switch.
 * POLISH lane (page weight): rows no longer repeat their text in data-q / data-v (sort and filter read the cells;
 * the price cell keeps its numeric data-v), and the grid's cards are fetched on first use from
 * sections/ls-series-grid.liquid (Section Rendering API on this page's URL).
 */
/** Preserve the selected review theme for the separately rendered image grid. */
function lsSeriesGridUrl(href, section, theme) {
  const page = new URL(href);
  const url = new URL(page.pathname, page.origin);
  // Shopify's preview bar can remove the query parameter from the visible URL.
  const preview = page.searchParams.get('preview_theme_id') ||
    (theme?.role === 'unpublished' && theme.id ? String(theme.id) : '');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  url.searchParams.set('section_id', section || 'ls-series-grid');
  return url;
}

class LsSeriesModels extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const tools = this.querySelector('[data-tools]');
    if (tools) tools.hidden = false;
    this.tbody = this.querySelector('[data-panel="table"] tbody');
    this.count = this.querySelector('[data-count]');
    this.querySelectorAll('thead button[data-sort]').forEach((b) =>
      b.addEventListener('click', () => this.sort(/** @type {HTMLButtonElement} */ (b)), { signal })
    );
    this.querySelector('[data-filter]')?.addEventListener('input', (e) => this.filter(/** @type {HTMLInputElement} */ (e.target).value), { signal });
    this.querySelectorAll('[data-view]').forEach((b) =>
      b.addEventListener('click', () => this.view(/** @type {HTMLElement} */ (b).dataset.view || 'table'), { signal })
    );
    this.querySelector('[data-showall]')?.addEventListener('click', () => this.uncap(), { signal });
    this.updateCount();
  }

  uncap() {
    this.querySelector('[data-panel="table"]')?.classList.remove('is-capped');
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {HTMLButtonElement} button */
  sort(button) {
    if (!this.tbody) return;
    this.uncap();
    const th = /** @type {HTMLElement} */ (button.closest('th'));
    const col = Number(button.dataset.sort);
    const dir = th.getAttribute('aria-sort') === 'ascending' ? -1 : 1;
    this.querySelectorAll('thead th[aria-sort]').forEach((x) => x.setAttribute('aria-sort', 'none'));
    th.setAttribute('aria-sort', dir === 1 ? 'ascending' : 'descending');
    const num = (/** @type {string} */ s) => {
      const m = s.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
      return m ? parseFloat(m[0]) : NaN;
    };
    const rows = [...this.tbody.rows];
    const key = rows.map((r, i) => {
      const cell = r.cells[col];
      const v = cell ? cell.dataset.v ?? (cell.textContent || '').trim() : '';
      // Model-number separators are not negative signs: sort the model column naturally.
      return { r, i, v, n: col === 0 ? NaN : num(v) };
    });
    key.sort((a, b) => {
      if (!a.v !== !b.v) return a.v ? -1 : 1; // empty cells last either way
      const d = !isNaN(a.n) && !isNaN(b.n) && a.n !== b.n ? a.n - b.n : a.v.localeCompare(b.v, undefined, { numeric: true });
      return d ? d * dir : a.i - b.i;
    });
    this.tbody.append(...key.map((k) => k.r));
  }

  /** Search text of a table row: model + key-spec cells (not price / link), lower case; computed once.
   * @param {HTMLTableRowElement} row */
  rowText(row) {
    this.texts ??= new WeakMap();
    let t = this.texts.get(row);
    if (t === undefined) {
      t = [...row.cells].slice(0, -2).map((c) => (c.textContent || '').trim()).join(' ').toLowerCase();
      this.texts.set(row, t);
    }
    return t;
  }

  /** @param {string} text */
  filter(text) {
    this.words = text.toLowerCase().split(/\s+/).filter(Boolean);
    if (this.words.length) this.uncap();
    const words = this.words;
    const match = (/** @type {string} */ hay) => words.every((w) => hay.includes(w));
    for (const r of this.tbody?.rows || []) r.hidden = !match(this.rowText(r));
    this.querySelectorAll('[data-panel="grid"] [data-q]').forEach((el) => {
      /** @type {HTMLElement} */ (el).hidden = !match(/** @type {HTMLElement} */ (el).dataset.q || '');
    });
    this.updateCount(words.length > 0);
  }

  /** @param {boolean} [filtered] */
  updateCount(filtered = false) {
    if (!this.count || !this.tbody) return;
    const all = this.tbody.rows.length;
    const shown = [...this.tbody.rows].filter((r) => !r.hidden).length;
    this.count.textContent = filtered ? `${shown} / ${all}` : '';
  }

  /** @param {string} name */
  view(name) {
    this.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (b).dataset.view === name)));
    this.querySelectorAll('[data-panel]').forEach((p) => {
      /** @type {HTMLElement} */ (p).hidden = /** @type {HTMLElement} */ (p).dataset.panel !== name;
    });
    if (name === 'grid') this.loadGrid();
  }

  /** First switch to Images: fetch the cards (sections/ls-series-grid.liquid) and put them before the grid note. */
  async loadGrid() {
    const panel = /** @type {HTMLElement|null} */ (this.querySelector('[data-panel="grid"][data-grid-section]'));
    if (!panel || this.gridLoading) return;
    this.gridLoading = true;
    panel.setAttribute('aria-busy', 'true');
    try {
      const url = lsSeriesGridUrl(location.href, panel.dataset.gridSection, globalThis.Shopify?.theme);
      const res = await fetch(url, { signal: this.#abort?.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      // the cards' CSS ({% stylesheet %} in ls-product-card) is not in this page's stylesheet subset: take the
      // response's section stylesheet once
      const css = doc.querySelector('style[data-section-stylesheet]');
      if (css && !document.querySelector('style[data-ls-series-grid-css]')) {
        const st = document.createElement('style');
        st.dataset.lsSeriesGridCss = '';
        st.textContent = css.textContent;
        document.head.append(st);
      }
      // compare checkboxes: assets/ls-catalog.js (delegated events), one module script per card in the response
      // (inserted scripts never run): load it once here, then sync the ticks
      const src = doc.querySelector('script[src*="ls-catalog"]')?.getAttribute('src');
      doc.querySelectorAll('.ls-series-grid__item script').forEach((s) => s.remove());
      const items = [...doc.querySelectorAll('.ls-series-grid__item')];
      panel.prepend(...items);
      panel.removeAttribute('data-grid-section');
      if (this.words?.length) this.filter(this.words.join(' '));
      if (src) await import(new URL(src, location.href).href);
      document.dispatchEvent(new CustomEvent('ls:cards-added'));
    } catch (err) {
      if (/** @type {Error} */ (err)?.name === 'AbortError') return;
      this.view('table'); // keep the table (it lists every model) when the cards cannot load
    } finally {
      panel.removeAttribute('aria-busy');
      this.gridLoading = false;
    }
  }
}

if (!customElements.get('ls-series-models')) customElements.define('ls-series-models', LsSeriesModels);
